import { Api } from "../api";
import { AxiosResponse } from "axios";

/**
 * @typedef {{
 *  id: number,
 *  imisId: string,
 *  name: string,
 *  specialRole: string,
 *  assignedProjects: number,
 *  assignedFinancials: number}} User
 */

const userApi = new Api("api/Users");
const accountApi = new Api("Account");
const checkSessionApi = new Api("api/Users/checksession", false);

/**
 * Get a list of AMO staff users (Enabled or Admin role only).
 *
 * @returns {Promise<AxiosResponse<User[]>>}
 */
export const getAmoUsersEnabledOrAdmin = () => userApi.get("amo/management");

/**
 * A list of treasurer users.
 *
 * @returns {Promise<AxiosResponse<User[]>>}
 */
export const getTreasurers = () => userApi.get("treasurer");

/**
 * A list of municipal users for a given municipality ID
 *
 * @param {number} municipalityId municipality ID to search by
 *
 * @returns {Promise<AxiosResponse<User[]>>}
 */
export const getMunicipalUsers = (municipalityId) =>
  userApi.get(`municipal/${municipalityId}`);

/**
 * A list of communications users, or given an id.
 *
 * @returns {Promise<AxiosResponse<User[]>>}
 */
export const getCommunicationsUsers = () => userApi.get("communications");

// Individual Resources

/**
 * Add a new user.
 *
 * @param {object} userData user data to be saved
 *
 * @returns {Promise<AxiosResponse<User>>}
 */
export const createNewUser = (userData) => accountApi.post("Create", userData);

/**
 * Get a user account.
 *
 * @param {string} userId
 *
 * @returns {Promise<AxiosResponse<User>>}
 */
export const getUser = (userId) => accountApi.get(userId);

/**
 * Remove a user Account.
 *
 * @param {string} userId
 *
 * @returns {Promise<AxiosResponse<User>>}
 */
export const deleteUser = (userId) => () =>
  accountApi.delete(`Remove/${userId}`);

/**
 * Get the account details.
 *
 * @param {string} accountId id of the account to retrieve
 *
 * @returns {Promise<AxiosResponse<User>>}
 */
export const getAccountDetails = (accountId) => accountApi.get(accountId);

/**
 * Update the account details.
 *
 * @param {string} accountId id of the account to update
 * @param {object} userData account data to be saved
 *
 * @returns {Promise<AxiosResponse<User>>}
 */
export const updateAccountDetails = (accountId, userData) =>
  accountApi.put(accountId, userData);

/**
 * Check that a username exists.
 *
 * @param {object} checkParams
 * @param {string} checkParams.usernameToCheckFor
 * @param {string} checkParams.userIdToCheckFor
 *
 * @returns {Promise<AxiosResponse<boolean>>}
 */
export const checkUsername = (checkParams) =>
  accountApi.post("UsernameExists", checkParams);

/**
 * Check that a email exists.
 *
 * @param {object} emailCheckParams
 * @param {string} emailCheckParams.emailToCheckFor
 * @param {string} emailCheckParams.userIdToCheckFor
 *
 * @returns {Promise<AxiosResponse<boolean>>}
 */
export const checkEmail = (emailCheckParams) =>
  accountApi.post("EmailExists", emailCheckParams);

/**
 * Check that a delegate exists for a municipality.
 *
 * @param {object} delegateCheckData
 * @param {string} delegateCheckData.municipalityIdToCheckFor
 * @param {string} delegateCheckData.userIdToCheckFor
 *
 * @returns {Promise<AxiosResponse<User>>}
 */
export const checkDelegate = (delegateCheckData) =>
  accountApi.post("DelegateExists", delegateCheckData);

/**
 * Check that a primary contact exists for a municipality.
 *
 * @param {object} primaryContactCheckData
 * @param {string} primaryContactCheckData.municipalityIdToCheckFor
 * @param {string} primaryContactCheckData.userIdToCheckFor
 *
 * @returns {Promise<AxiosResponse<string>>}
 */
export const checkPrimaryContact = (primaryContactCheckData) =>
  accountApi.post("PrimaryContactExists", primaryContactCheckData);

/**
 * Check that the user session is still active on the authority server.
 *
 * @returns {Promise<AxiosResponse<boolean>>}
 */
export const checkSession = () =>
  checkSessionApi.get(
    "",
    {},
    {
      // Set this to return 401 on this route instead of redirecting to login page
      headers: { "X-Requested-With": "XMLHttpRequest" },
    }
  );
